import { UserAvatar } from "domains/user/UserAvatar"
import * as BookingStatus from "enums/BookingStatus"
import { Text } from "components/Text"
import { useTranslator } from "components/Translator"
import { RawLink } from "components/Link"
import { useRouter } from "components/Router"
import PropTypes from "prop-types"
import cx from "classnames"
import * as AdvertAvailability from "enums/AdvertAvailability"
import { isAfter } from "date-fns/isAfter"
import { startOfToday } from "date-fns/startOfToday"
import { compareAsc } from "date-fns/compareAsc"
import * as React from "react"
import { isExternalUser } from "domains/user/model"
import { ExternalUserModal } from "domains/user/ExternalUserModal"

export const AdvertParticipants = ({ bookings, variant = "full", advert }) => {
  const translator = useTranslator()
  const today = startOfToday()

  const filters = [
    (booking) => booking.status.enum_value === BookingStatus.VALIDATED,
    advert.availability?.enum_value === AdvertAvailability.PERIOD
      ? (booking) => isAfter(new Date(booking.dateInterval.to), today)
      : null,
  ].filter((filter) => filter !== null)

  const bookingsToShow = bookings
    ? bookings.filter((booking) => filters.every((filter) => filter(booking)))
    : []

  const bookingsByDateInterval = sortBookingsByDateInterval(bookingsToShow)

  return (
    <div
      className={cx({
        "space-y-4": variant === "full",
        "flex flex-wrap": variant === "compact",
      })}
    >
      {bookingsByDateInterval.length > 0 ? (
        bookingsByDateInterval.map((booking) => {
          return (
            <div
              className={cx({
                "mr-2 last:mr-0 shrink-0": variant === "compact",
              })}
              key={booking.id}
            >
              <BookingParticipant
                user={booking.user}
                dateInterval={{
                  from: new Date(booking.dateInterval.from),
                  to: new Date(booking.dateInterval.to),
                }}
                variant={variant}
              />
            </div>
          )
        })
      ) : (
        <Text variant="body2">
          {translator.trans("AdvertParticipants.empty", {}, "components")}
        </Text>
      )}
    </div>
  )
}

AdvertParticipants.propTypes = {
  variant: PropTypes.oneOf(["full", "compact"]),
}

const BookingParticipant = ({ user, dateInterval, variant }) => {
  const router = useRouter()
  const [showExternalModal, setShowExternalModal] = React.useState(false)

  const handleClick = (e) => {
    if (isExternalUser(user)) {
      e.preventDefault()

      setShowExternalModal(true)
    }
  }

  return (
    <>
      <RawLink
        href={router.generate("app_account_profile_show", {
          userId: user.id,
        })}
        onClick={handleClick}
      >
        {variant === "compact" ? (
          <UserAvatar size="small" user={user} />
        ) : (
          <div className="flex space-x-4 items-center">
            <UserAvatar size="small" user={user} />
            <BookingParticipantInfos user={user} dateInterval={dateInterval} />
          </div>
        )}
      </RawLink>
      <ExternalUserModal
        isOpen={showExternalModal}
        onRequestClose={() => setShowExternalModal(false)}
      />
    </>
  )
}

function BookingParticipantInfos({ user, dateInterval }) {
  const translator = useTranslator()

  return (
    <div>
      <Text variant="headline6" className="text-primary-dark">
        {user.fullName}
      </Text>
      <Text variant="caption" className="text-light">
        {translator.trans(
          "AdvertParticipants.dateInterval",
          {
            from: dateInterval?.from.toLocaleDateString(),
            to: dateInterval?.to.toLocaleDateString(),
          },
          "components",
        )}
      </Text>
    </div>
  )
}

const sortBookingsByDateInterval = (bookings) => {
  return bookings.sort((a, b) => {
    return (
      compareAsc(
        new Date(a.dateInterval.from),
        new Date(b.dateInterval.from),
      ) || compareAsc(new Date(a.dateInterval.to), new Date(b.dateInterval.to))
    )
  })
}
